import { defineStore } from 'pinia';
import MemberDetail from '../models/MemberDetail';
import { usePinia } from '#imports';
import { handleErrorAndNotificationHandler } from '~/util/errorNotificationHandler';
import { AxiosError } from 'axios';
import type { Pinia } from './shared/sharedTypes';
import { DateTime } from 'luxon';
import type { AxiosResponse } from 'axios';
import type { ThresholdType } from './reports';

interface API {
  app: {
    initMember: string;
    updateStudyConfig: (channelId: number | string) => string;
    termsOfService: string;
    countryData: string;
    listTypeData: string;
    complianceRequiredActionTypes: string;
    complianceMembershipParticipationTypes: string;
    featureFlags: string;
    notificationStatusTypesRefData: string;
    getAllGroupedStudies: (query?: string) => string;
  };
  channels: {
    updateChannelStatus<T extends object>(
      channelId: number | string,
      params: T
    ): string;
    getStudyActions: (channelId: number) => string;
    getStudyOrderReportData: (channelId: number) => string;
    getBurnRateReportData: (channelId: number) => string;
    getRentalSummariesReportData: (channelId: number) => string;
    getRentalEquipmentSummariesReportData: (channelId: number) => string;
    getInvoiceReportData: (channelId: number) => string;
    getPickupReportData: (channelId: number) => string;
    channelSummary: (
      channelId: number | string,
      query?: Record<string, unknown>
    ) => string;
    channelMembers: (
      channelId: number | string,
      query?: Record<string, unknown>
    ) => string;
    studyTeamContacts: (
      studyId: number | string,
      teamId: number | string
    ) => string;
    channelMemberRoles: (
      channelId: number | string,
      memberId: string,
      query?: string
    ) => string;
    verifyMultipleSites: (
      channelId: number | string,
      query?: Record<string, unknown>
    ) => string;
    channelTeamMembers: (
      channelId: number | string,
      teamId: number | string,
      query?: Record<string, unknown>
    ) => string;
    channelMemberDetail: (
      channelId: number | string,
      memberId: number | string
    ) => string;
    channelKeyContacts: (
      channelId: number | string,
      query?: Record<string, unknown>
    ) => string;
    submitCatalogSalesOrder: (channelId: number | string) => string;
    submitQuotationSalesOrder: (channelId: number | string) => string;
    resendInvitation: (
      channelId: number | string,
      teamId: number | string,
      inviteId: number | string,
      query?: Record<string, unknown>
    ) => string;
    updateInvitedMemberRole: (
      channelId: number | string,
      teamId: number | string,
      inviteId: number | string
    ) => string;
    getStudyQuotationPdf: (
      studyId: number | string,
      quotationId: number | string
    ) => string;
    getChannelRoles: (channelId: number | string) => string;
    getChannelMemberRoles: (
      channelId: number | string,
      memberId: string
    ) => string;
    updateChannel: (channelId: number) => string;
    removeMemberFromTeam: (
      channelId: number | string,
      teamId: number | string,
      memberId: string
    ) => string;
    invitedChannelMemberDetail: (
      channelId: number | string,
      invitedMemberEmail: string
    ) => string;
    studyNotificationContact: (channelId?: number | string) => string;
    updateStudyNotificationContact: (
      channelId: number | string,
      notificationContactId: number | string
    ) => string;
    updateDeleteStudyTeamContacts: (
      channelId: number | string,
      teamId: number | string,
      notificationId: number | string
    ) => string;
    getConfiguredNotificationContacts: (
      channelId: number | string,
      query?: Record<string, unknown>
    ) => string;
    getStudyQuotations: (studyId: number | string) => string;
    getRentalAgreementtForStudy: (studyId: number | string) => string;
    getInvoicesForStudy: (studyId: number | string) => string;
    supportRequest: (channelId?: number | string) => string;
    getRentalEquipmentData: (
      channelId: number | string,
      query?: Record<string, unknown>
    ) => string;
    getProductData: (
      channelId: number | string,
      query?: Record<string, unknown>
    ) => string;
    getChannel: (channelId: number | string) => string;
    removeBannerImage: (channelId: number | string) => string;
    removeLogoImage: (channelId: number | string) => string;
    createTeam: (channelId: number | string) => string;
    editTeamInfo: (
      channelId: number | string,
      teamId: number | string
    ) => string;
    updateStatusStudyTeam: (
      channelId: number | string,
      teamId: number | string,
      query?: string
    ) => string;
  };
  user: {
    subscribeToChannel(channelId?: number | string): string;
    getCollectionOfChannels(): string;
  };
}

type ChannelActionKey =
  | 'add-channel-classification-schema'
  | 'add-channel-team'
  | 'add-document-post'
  | 'add-news-post'
  | 'add-study-budget-threshold-notifications'
  | 'create-order'
  | 'create-pickup'
  | 'delete-study-budget-threshold-notifications'
  | 'edit-channel'
  | 'edit-channel-workflow-status'
  | 'edit-study-budget-threshold-notifications'
  | 'view-channel'
  | 'view-study-budget-threshold-notifications'
  | 'view-study-directory-audit-log'
  | 'view-study-reports';
type ChannelActions = Record<ChannelActionKey, object>;
interface NotificationResp {
  notificationContactConfigs: NotificationConfig[];
}
interface CreateCatalogOrderStatus {
  status: number;
}
interface NotificationConfig {
  emailAddress: string;
  name: string;
  serviceEntityTypeStatusIds: number[];
  studyContactId: number;
}
interface WorkflowNotificationContactConfigResp {
  workflowNotificationContactConfigs: WorkflowNotificationContactConfig[];
}
interface WorkflowNotificationContactConfig {
  contactType: {
    contactTypeClass: string;
    contactTypeClassPK: number;
  };
  emailAddress: string;
  name: string;
  serviceEntityTypeStatusIds: number[];
  _tempId: number;
}
interface QuotationResp {
  quotations: QuotationItem[];
}
interface InvoiceResponse {
  invoices: InvoicesItem[];
}
interface RentalResponse {
  rentalAgreements: RentalAgreementItem[];
}
interface RentalAgreementItem {
  currency: string;
  id: number;
  lot: string;
  name: string;
  siteCode: string;
  siteName: string;
  startDateMillis: number;
  status: string;
  statusCode: string;
  totalDisplay: string;
}
interface InvoicesItem {
  amountDueDisplay: string;
  currency: string;
  dueDateMillis: number;
  id: number;
  invoiceDateMillis: number;
  name: string;
  reference: string;
  status: string;
  statusCode: string;
  totalDisplay: string;
}
interface QuotationItem {
  code: string;
  companyName: string;
  createDateMillis: number;
  currencyCode: string;
  emsereSalesPerson: string;
  expirationDateMillis: number;
  id: number;
  quotationDateMillis: number;
  status: string;
  total: string;
}
interface AttachmentFile {
  extension: string;
  getRawFile: () => Promise<File>;
  name: string;
  progress: number;
  size: number;
  status: number;
  uid: string;
}
interface ClientChannel {
  actions: ChannelActions;
  channelRoleCategoryMemberCounts: {
    ADMINISTRATIVE: number;
    SITE: number;
  };
  channelSalesOrderType: string;
  channelSponsor: {
    name: string;
    organizationId: number;
  };
  channelStatus: string;
  clientChannelId: number;
  customerStudyId: number;
  name: string;
  teamTypeCounts: {
    STUDY_CRO: number;
    STUDY_SERVICE_PROVIDER: number;
    STUDY_SITE: number;
    STUDY_SPONSOR: number;
  };
}
interface ClientOwnedChannel {
  items: ClientChannel[];
  lastPage: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
}
interface ChannelRole {
  canAssign: boolean;
  category: string;
  channelRoleId: number;
  description: string;
  name: string;
  title: string;
}
interface OrderPayload {
  orderItems: OrderItem[];
  remarks: string;
  requestedShipmentDate: string;
  selectedSites: SelectedSite[];
  studyContacts: SiteContact[];
}
interface OrderItem {
  acquisitionMethod: string;
  loggerSettings: LoggerSettings;
  minimumRentalPeriod: number;
  odooProductId: number;
  packagingOptionId?: number;
  quantity: number;
}
interface LoggerSettings {
  configurationType: string;
  customLoggerSettings: CustomLoggerSettings;
  loggerSettingsConfigId: string;
}
interface CustomLoggerSettings {
  high: number;
  interval: number;
  low: number;
}
interface SelectedSite {
  purchaseOrderNumber: string;
  siteContacts: SiteContact[];
  teamId: number;
}
interface SiteContact {
  emailAddress: string;
  name: string;
  serviceEntityTypeStatusIds: number[];
}
interface StudyContact extends SiteContact {
  studyContactId: number;
}
interface ChannelClient {
  clientId: number;
  clinicalTrialEntityId: number;
  name: string;
}
interface UserChannelRole {
  roleId: number;
  title: string;
}
interface ChannelTeamMemberships {
  teamId: number;
  teamName: string;
  teamRoleId: number;
}
interface UserTaxonomies {
  description: string;
  name: string;
  taxonomyId: number;
}
interface ComplianceRangeSummary {
  lowRangeCount: number;
  midRangeCount: number;
  highRangeCount: number;
  categoryType: {
    categoryType: string;
  };
}
interface UserData {
  userChannelCapabilities: {
    canSharePostsWithEveryone: boolean;
  };
  userChannelComplianceData: {
    userChannelComplianceScore: number;
    userChannelComplianceStats: {
      channelComplianceStats: {
        totalItemsCount: number;
        requiredActionsCount: number;
        completedItemsCount: number;
        completedActionsCount: number;
        incompletedActionsCount: number;
        incompletedItemsCount: number;
        completedPercentage: number;
        unreadItemsCount: number;
      };
      complianceCategorySummaryStats: {
        memberComplianceRangeSummary: ComplianceRangeSummary;
        teamComplianceRangeSummary: ComplianceRangeSummary;
        postComplianceRangeSummary: ComplianceRangeSummary;
        teamCountrySummary: {
          teamSummaries: {
            threeDigitCountryCode: string;
            teamCount: number;
            complianceScore: number;
          };
          mapDisplayConfigs: {
            centerLatitude: number;
            centerLongitude: number;
          };
        };
      };
    };
    userChannelRequiredActionsCount: number;
    userChannelUnreadItemsCount: number;
    userChannelRoles: UserChannelRole[];
  };
  userChannelTeamMemberships: ChannelTeamMemberships[];
  userSubscribedToNotifications: boolean;
  userVisibleTaxonomies: UserTaxonomies;
}
interface StatusOption {
  statusName: string;
  statusNameLabel: string;
  updateStatusUrl: string;
}
interface Channel {
  actions: ChannelActions;
  availableRoles: ChannelRole[];
  briefTitle: string;
  channelBanner: {
    displayType: string;
    imageUrl: string;
  };
  channelId: number;
  channelOwnerClient: ChannelClient;
  channelSponsorName: string;
  channelSponsorTeamId: number;
  createDateTimeMillis: number;
  favoriteChannel: boolean;
  invoiceReferenceNumber: number | string;
  logoUrl: string;
  name: string;
  purchaseOrderNumber: string;
  salesOrderType: string;
  statusId: number;
  statusName: string;
  studyEstimatedEndDateMillis: number;
  studyStartDateMillis: number;
  timeRequiredOnPostToLogAuditViewInMillis: number;
  userRelatedData: UserData;
  uuid: string;
  workflow: {
    statusOptions: StatusOption[];
  };
  bannerFile?: File | Blob;
  logoFile?: File | Blob;
  channelFeatureFlags?: Record<string, boolean>;
}
interface ContactData {
  address: {
    addressId: number;
    buildingName: string;
    contactTypeDisplayName: string;
    countryId: number;
    countryName: string;
    locality: string;
    postalCode: string;
    primary: boolean;
    region: string;
    streetAddressLine1: string;
    streetAddressLine2: string;
    streetAddressLine3: string;
  };
  contactType: string;
  phone: {
    contactTypeDisplayName: string;
    extension: string;
    number: string;
    phoneId: number;
    primary: boolean;
  };
}
interface UserProfile {
  acceptedTermsOfService: boolean;
  basicUserInfo: {
    displayName: string;
    displayPrefix: string;
    displaySuffix: string;
    emailAddress: string;
    firstName: string;
    jobTitle: string;
    languageId: string;
    lastName: string;
    middleName: string;
    portraitUrl: string;
    prefixId: number;
    suffixId: number;
    timezoneId: string;
    userId: number;
    uuid: string;
  };
  contactData: ContactData;
  lastLoginDateTimeMillis: number;
  missingInviteAsNewUser: boolean;
  verifiedEmail: boolean;
}
interface KeyContactsItem {
  channelId: number;
  keyContactId: number;
  roleInChannel: string;
  showOnChannelPage: boolean;
  userProfile: UserProfile;
}
interface KeyContactsPage {
  items: KeyContactsItem[];
  lastPage: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
}
type KeyContactsCache = Record<string, KeyContactsPage>;
interface Membership {
  auditUserId: number;
  startDateTimeMillis: number;
  team: {
    studyServiceProviderId: number;
    studyTeam: StudyTeam;
  };
}

interface StudyMember {
  active: boolean;
  channelMemberDetailUrl: string;
  channelMemberId: string;
  memberStatusType: string;
  memberships: Membership[];
  roleNames: string[];
  userProfile: UserProfile;
}
interface StudyMembersCached {
  facets: Facet[];
  items: StudyMember[];
  lastPage: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
}
type ChannelMemberCached = Record<`${string}>${string}`, StudyMembersCached>[];
interface StudyNotificationContact {
  emailAddress: string;
  name: string;
  serviceEntityTypeStatusIds: number[];
  studyContactId: number;
}
type ConfigureNotificationContacts = Omit<
  StudyNotificationContact,
  'studyContactId'
> & {
  contactType: {
    contactTypeClass: string;
    contactTypeClassPK: number;
  };
  _tempId: number;
};
interface FacetEntry {
  term: string;
  numOccurrences: number;
  displayValue: string;
}
interface Facet {
  key: string;
  facetEntries: FacetEntry[];
  displayName: string;
}
interface MemberInvite {
  email: string;
  teamId: number[];
  roleIds: number[];
  messageForMember: string;
  sendEmailCheckbox: boolean;
  channelId: string | number;
  invitedUserLastName: string;
  invitedUserFirstName: string;
  save(): Promise<void>;
}
interface Quotation {
  code: string;
  companyName: string;
  createDateMillis: number;
  currencyCode: string;
  emsereSalesPerson: string;
  expirationDateMillis: number;
  id: number;
  quotationDateMillis: number;
  status: string;
  total: string;
}
interface PreconfiguredSetting {
  displayValue: string;
  loggerSettingsConfig: string;
}
interface ProductPaginator {
  isLastPage: boolean;
  page: number;
  pageSize: number;
  productLoggerSettings: {
    preconfiguredSettings: PreconfiguredSetting[];
  };
  totalCount: number;
}
interface PackageOption {
  packagingOptionId: number;
  packagingQuantity: number;
  packagingQuantityName: string;
}
interface StudyProductsResp extends ProductPaginator {
  products: StudyProduct[];
}
interface StudyProduct {
  category: string;
  code: string;
  name: string;
  packageOptions: PackageOption[];
  productId: number;
  productRequiresLoggerConfiguration: boolean;
  productType: string;
  smallProductImageBase64Binary: string;
  summary: string;
  supportedAcquisitionMethods: {
    canPurchase: boolean;
    canRent: boolean;
  };
}

type amountType = {
  amount: string;
  amountMinor: number;
  currency: string;
};
type ReportResults<T> = {
  reportResults: T[];
};
export interface SubscriptionItem {
  awbNumber: string;
  calibrationInterval: number;
  city: string;
  contact: string;
  contractNumber: string;
  contractStatus: string;
  country: string;
  countryCode: string;
  courier: string;
  customer: string;
  damageAmount: amountType;
  deposit: amountType;
  depositRefund: amountType;
  division: string;
  expectedEndDate: string | null;
  expectedEndDateTimeMillis?: number;
  expectedMrpEndDate: string | null;
  expectedMrpEndDateTimeMillis?: number;
  firstInvoiceDate: string | null;
  firstInvoiceDateTimeMillis?: number;
  institution: string;
  invoiceCurrency: string;
  invoiceTerm: string;
  lastCalibrationDate: string | null;
  lastCalibrated?: string;
  lastInvoiceDate: string | null;
  lastInvoicedDateTimeMillis?: number;
  minimumRentalPeriod: string;
  nextCalibrationDate: string | null;
  nextCalibrationDateTimeMillis?: number;
  numberOfInvoicedMonths: string;
  orderedBy: string;
  poNumber: string;
  product: string;
  reCalibrationService: string;
  receivedDate: string | null;
  receivedDateTimeMillis?: number;
  requestedPickupDate: string | null;
  requestedPickupDateTimeMillis?: number;
  rentalPrice: amountType;
  serialNumber: string;
  siteNumber: string;
  sponsor: string;
  startDate: string | null;
  startDateTimeMillis?: number;
  state: string;
  studyCodeExt: string;
  studyName: string;
  totalInvoicedAmount: amountType;
  transportIn: amountType;
  transportOut: amountType;
  postalCode: string;
  actualShipmentDate: string | null;
  actualShipmentDateTimeMillis?: number;
}

export interface SalesOrderItem {
  actualShipmentDate: string | null;
  actualShipmentDateTimeMillis?: number;
  address: string;
  awbNumber: string;
  city: string;
  contact: string;
  country: string;
  countryCode: string;
  courier: string;
  currency: string;
  customer: string;
  division: string;
  expirationDate: string | null;
  expirationDateTimeMillis?: number;
  institution: string;
  lastCalibrationDate: string | null;
  lastCalibrated?: string;
  orderState: string;
  orderedBy: string;
  poNumber: string;
  postalCode: string;
  pricePerUnit: amountType;
  product: string;
  quantity: number;
  saleOrderNumber: string;
  serialOrLotNumber: string;
  siteNumber: string;
  sponsor: string;
  state: string;
  status: string;
  studyName: string;
  studyCodeExt: string;
  transactionType: string;
  unitOfMeasure: string;
}

export interface SubscriptionEquipmentItem {
  calibrationInterval: number;
  country: string;
  countryCode: string;
  lastCalibrationDate: string | null;
  lastCalibrationDateMillis?: number;
  product: string;
  serialNumber: string;
  siteNumber: string;
  status: string;
  studyName: string;
  subscription: string;
}

export interface InvoicesDataItem {
  amountEach: amountType;
  companyName: string;
  siteCountry: string;
  siteCountryCode: string;
  currency: string;
  customerName: string;
  description: string;
  invoiceDate: string | null;
  invoiceDateTimeMillis?: number;
  invoiceNumber: string;
  invoicePoNumber: string;
  invoiceType: string;
  lineVat: string;
  quantity: number;
  reference: string;
  siteNumber: string;
  sponsorStudyCode: string;
  studyName: string;
  total: amountType;
  transactionType: string;
  unitOfMeasure: string;
}

export interface PickupDataItem {
  awbNumber: string;
  city: string;
  closeDate: string | null;
  closeDateTimeMillis?: number;
  contact: string;
  country: string;
  countryCode: string;
  customer: string;
  durationInDays: number;
  entryDate: string | null;
  entryDateTimeMillis?: number;
  exchange: string;
  institution: string;
  pickupStatus: string;
  postCode: string;
  product: string;
  receiptStatus: string;
  serialNumber: string;
  siteNumber: string;
  sponsorStudyCode: string;
  startDate: string | null;
  startDateTimeMillis?: number;
  state: string;
  studyName: string;
  subscription: string;
  type: string;
}

export type BudgetCategory =
  | 'SALE'
  | 'RENTAL'
  | 'SERVICE'
  | 'TRANSPORT'
  | 'OTHER';
export interface BudgetItem {
  budgetDate: string;
  budgetDateMillis?: number;
  budgetAmount: amountType;
  createDateMillis?: number;
  reference: string;
  budgetCategory?: BudgetCategory;
  budgetCategories?: {
    budgetCategory: BudgetCategory;
    budgetCategoryAmount: amountType;
  }[];
}

export interface StudyBudget {
  budgetItems: BudgetItem[];
  sumOfBudgetItems: amountType;
  studyStartDateMillis?: number;
  studyEstimatedEndDateMillis?: number;
  purchaseOrderNumbers?: string[];
}

export interface SpendItem {
  invoiceDate?: string | null;
  spendDate?: string | null;
  invoiceNumber?: string;
  companyName?: string;
  reference?: string;
  poNumber?: string;
  invoiceDateTimeMillis?: number;
  studyName?: string;
  siteNumber?: string;
  lineVat?: string;
  quantity?: number;
  unitOfMeasure?: string;
  product?: string;
  serialNumber?: string;
  amountEach?: amountType;
  total?: amountType;
  currency?: string;
  country?: string;
  transactionType?: string;
  invoiceType?: string;
  spendCategory?: BudgetCategory;
}
export interface ActualSpend {
  spendItems?: SpendItem[];
  sumOfspendItems?: amountType;
}

export interface ProjectionItem {
  projectionDate?: string | null;
  projectionAmount: amountType;
  projectedDateMillis?: number;
  projectionCategory?: BudgetCategory;
  contractNumber?: string;
  product?: string;
  siteNumber?: string;
  // Not in the response yet; the grid columns light up once they arrive
  poNumber?: string;
  serialNumber?: string;
}
export interface ProjectedSpend {
  projectionItems: ProjectionItem[];
  sumOfProjectionItems: amountType;
}
export interface NotificationThreshold {
  label: string;
  percent: number;
  recipientName: string;
  recipientEmail: string;
  includeInWeeklyEmail: boolean;
  approachingRepeatWeekly: boolean;
  exceededRepeatDaily: boolean;
}

export interface BurnRateSettings {
  studyStartDateMillis: number;
  studyEndDateMillis: number;
  thresholds: NotificationThreshold[];
  scenarios: {
    numberOfSites: number;
    remainingMonths: number;
  };
}

export interface ReportThreshold {
  thresholdType: ThresholdType;
  thresholdValue: number;
}

export interface BurnRateReportResponse {
  studyStartDate?: string | null;
  studyEstimatedEndDate?: string | null;
  studyBudget: StudyBudget;
  studyStartDateMillis?: number;
  studyEstimatedEndDateMillis?: number;
  purchaseOrderNumbers: string[];
  actualSpend: ActualSpend;
  projectedSpend: ProjectedSpend;
  thresholds: ReportThreshold[];
}
interface StudyTeam {
  activeMemberCount: number;
  channelId: number;
  channelName: string;
  contacts: {
    contactData: ContactData;
    contactTypes: string[];
    primaryContactTypeForTeam: string;
  };
  createDateTimeMillis: number;
  createdByUserId: number;
  displayName: string;
  isActive: boolean;
  isInvestigatorSite: boolean;
  name: string;
  notificationContacts: unknown[];
  participantType: string;
  roleId: number;
  statusId: number;
  statusName: string;
  studyTeamCompliance: {
    complianceTrackingEnabled: boolean;
  };
  studyTeamDetailUrl: string;
  studyTeamId: number;
  teamType: string;
  workflow: {
    statusOption: StatusOption[];
  };
}
interface Team {
  code: string;
  countryId: number;
  countryName: string;
  principalInvestigatorName: string;
  siteActivationDateTimeMillis: number;
  siteClosureDateTimeMillis: number;
  siteSelectionDateTimeMillis: number;
  studySiteId: number;
  studyTeam: StudyTeam;
}
interface TeamDetail {
  auditUserId: number;
  startDateTimeMillis: number;
  team: Team;
}
interface DetailMember {
  activeMember: boolean;
  channelMemberDetailUrl: string | undefined;
  channelMemberId: string;
  membershipsByStatus: {
    CURRENT: TeamDetail[];
    INVITED: TeamDetail[];
    PAST: TeamDetail[];
  };
  rolesByStatus: {
    CURRENT: UserChannelRole[];
    INVITED: UserChannelRole[];
    PAST: UserChannelRole[];
  };
  userProfile: UserProfile;
}
interface Paginator {
  lastPage: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
}
interface NonRentalReason {
  id: number;
  reason: string;
}
export interface RentalEquipmentItem {
  channelId: number;
  contractOrSalesOrderNumber: string;
  deliveredDateMillis: number;
  expectedEndDateMillis: number;
  productCode: string | number;
  productName: string;
  serialOrLotNumber: string | number;
  serviceItemId: string;
  shipmentRequestId: number;
  subscriptionId: number;
  teamId: number;
  teamName: string;
}
interface RentalEquipment {
  isLastPage: boolean;
  nonRentalEquipmentReturnReasonsRefData: NonRentalReason[];
  page: number;
  pageSize: number;
  rentalEquipmentItems: RentalEquipmentItem[];
  totalCount: number;
}
export type StudySummary = Record<string, Channel>;
interface ChannelsState {
  channels: Channel[];
  clientChannels: ClientChannel[];
  activeChannel: Channel;
  syncing: boolean;
  activeChannelMemberPostsFetched: boolean;
  allChannelMemberPostsFetched: boolean;
  paginator: Paginator[];
  keyContacts: KeyContactsCache;
  channelSummary: StudySummary;
  inviteMessage: string;
  channelMembersCached: ChannelMemberCached;
  memberRoles: ChannelRole[];
  channelRoles: ChannelRole[];
  memberSearchTerm: string;
  memberSort: string;
  activeMemberGridKey: string;
  studyNotificationContacts: StudyNotificationContact[];
  teamNotificationContacts: StudyNotificationContact[];
  configuredNotificationContacts: ConfigureNotificationContacts[];
  studyQuotations: Quotation[];
  rentalAgreement: RentalAgreementItem[];
  studyInvoices: InvoicesItem[];
  studyEquipment: StudyProduct[];
  studySupplies: StudyProduct[];
  rentalEquipment: RentalEquipment;
  allStudyRentalEquipment: RentalEquipmentItem[];
  allStudyRentalEquipmentLoading: boolean;
  allStudyRentalEquipmentError: boolean;
  studyEquipmentPaginator: ProductPaginator;
  studySuppliesPaginator: ProductPaginator;
  studySuppliesSyncing: boolean;
  studyEquipmentSyncing: boolean;
  channelTeamMembers: [];
  memberDetail: DetailMember;
  memberDetailInvited: unknown;
  studyReportsData: SalesOrderItem[];
  burnRateReportData: BurnRateReportResponse | null;
  rentalSummariesData: SubscriptionItem[];
  rentalEquipmentSummariesData: SubscriptionEquipmentItem[];
  invoiceReportData: InvoicesDataItem[];
  pickupReportData: PickupDataItem[];
  selectedEquipmentItem: RentalEquipmentItem | null;
}
interface GetChannelSummary {
  countryId?: number | string;
  channelId: number | string;
  availableRolesCategory: string | undefined;
}
interface GetStudyMembers {
  pageSize: number;
  sort: string;
  memberStatusType: string;
  channelId: number | string;
  search: string;
  page: number;
}
interface GetStudyTeamMembers extends GetStudyMembers {
  teamId: number | string;
}

const assignNAValue = <T extends object>(
  array: readonly T[] | null | undefined
): T[] => {
  if (!Array.isArray(array)) return [];

  return array.map(
    (obj) =>
      // Object.fromEntries loses the row's named keys; this mapping keeps them.
      Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [
          key,
          value === null || value === undefined || value === '' ? 'N/A' : value,
        ])
      ) as T
  );
};
export const useChannelsStore = defineStore('channels', {
  state: (): ChannelsState => ({
    channels: [],
    clientChannels: [],
    activeChannel: {} as Channel,
    syncing: false,
    activeChannelMemberPostsFetched: false,
    allChannelMemberPostsFetched: false,
    paginator: [],
    keyContacts: {} as KeyContactsCache,
    channelSummary: {},
    inviteMessage: '',
    channelMembersCached: {} as ChannelMemberCached,
    memberRoles: [],
    channelRoles: [],
    memberSearchTerm: '',
    memberSort: '',
    activeMemberGridKey: '',
    studyNotificationContacts: [],
    teamNotificationContacts: [],
    configuredNotificationContacts: [],
    studyQuotations: [],
    rentalAgreement: [],
    studyInvoices: [],
    studyEquipment: [] as StudyProduct[],
    studySupplies: [] as StudyProduct[],
    rentalEquipment: {} as RentalEquipment,
    allStudyRentalEquipment: [] as RentalEquipmentItem[],
    allStudyRentalEquipmentLoading: false,
    allStudyRentalEquipmentError: false,
    studyEquipmentPaginator: {} as ProductPaginator,
    studySuppliesPaginator: {} as ProductPaginator,
    studySuppliesSyncing: false,
    studyEquipmentSyncing: false,
    channelTeamMembers: [],
    memberDetail: {} as DetailMember,
    memberDetailInvited: {},
    studyReportsData: [],
    burnRateReportData: {
      studyBudget: {
        budgetItems: [],
        sumOfBudgetItems: {
          amount: '0',
          amountMinor: 0,
          currency: '',
        },
      },
      studyStartDateMillis: 0,
      studyEstimatedEndDateMillis: 0,
      purchaseOrderNumbers: [],
      actualSpend: {
        spendItems: [],
        sumOfspendItems: {
          amount: '0',
          amountMinor: 0,
          currency: '',
        },
      },
      projectedSpend: {
        projectionItems: [],
        sumOfProjectionItems: {
          amount: '0',
          amountMinor: 0,
          currency: '',
        },
      },
      thresholds: [],
    } as BurnRateReportResponse,
    rentalSummariesData: [] as SubscriptionItem[],
    rentalEquipmentSummariesData: [] as SubscriptionEquipmentItem[],
    invoiceReportData: [] as InvoicesDataItem[],
    pickupReportData: [] as PickupDataItem[],
    selectedEquipmentItem: null,
  }),
  actions: {
    clearBurnRateReportData() {
      this.burnRateReportData = null;
    },
    async syncChannels(channels?: Channel[]) {
      try {
        const pinia = usePinia() as Pinia<API>;
        if (!channels) {
          const resp = await pinia.$axios.$get(pinia.$API.app.initMember);
          const data = resp as { channelSummaries: Channel[] };
          this.channels = data.channelSummaries as Channel[];
        }
        this.channels = channels ?? [];
      } catch (error) {
        handleErrorAndNotificationHandler('Error syncing channels:', error);
        // return null;
      }
    },
    async fetchStudyActions(channelId: number) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp: ChannelActions = await pinia.$axios.$get(
          pinia.$API.channels.getStudyActions(channelId)
        );

        this.activeChannel.actions = resp;
      } catch (error) {
        console.log('error', error);
      }
    },
    async fetchChannelActions(channelId: number) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const channel = this.channels.find((c) => c.channelId === channelId);

        if (!channel) {
          return;
        }

        const resp: ChannelActions = await pinia.$axios.$get(
          pinia.$API.channels.getStudyActions(channelId)
        );

        channel.actions = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching channel actions:',
          error
        );
      }
    },
    async fetchBurnRateReportData(channelId: number) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp = await pinia.$axios.$get<BurnRateReportResponse>(
          pinia.$API.channels.getBurnRateReportData(channelId)
        );

        if (resp) {
          this.burnRateReportData = {
            ...resp,
            studyBudget: resp.studyBudget || {
              budgetItems: [],
              sumOfBudgetItems: { amount: '0', amountMinor: 0, currency: '' },
            },
            actualSpend: resp.actualSpend || {
              spendItems: [],
              sumOfspendItems: { amount: '0', amountMinor: 0, currency: '' },
            },
            projectedSpend: resp.projectedSpend || {
              projectionItems: [],
              sumOfProjectionItems: {
                amount: '0',
                amountMinor: 0,
                currency: '',
              },
            },
          };
        } else {
          this.burnRateReportData = null;
        }
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching budget report data:',
          error
        );
      }
    },
    async fetchStudyOrderReportData(channelId: number) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp =
          await pinia.$axios.$get<ReportResults<SalesOrderItem> | null>(
            pinia.$API.channels.getStudyOrderReportData(channelId)
          );
        this.studyReportsData = assignNAValue(resp?.reportResults);
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching study order report data:',
          error
        );
      }
    },
    async fetchRentalSummariesReportData(channelId: number) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp = await pinia.$axios.$get<ReportResults<SubscriptionItem>>(
          pinia.$API.channels.getRentalSummariesReportData(channelId)
        );
        this.rentalSummariesData = assignNAValue(resp?.reportResults);
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching rental summaries report data:',
          error
        );
      }
    },
    async fetchRentalEquipmentSummariesReportData(channelId: number) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp = await pinia.$axios.$get<
          ReportResults<SubscriptionEquipmentItem>
        >(pinia.$API.channels.getRentalEquipmentSummariesReportData(channelId));
        this.rentalEquipmentSummariesData = assignNAValue(resp?.reportResults);
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching rental equipment summaries report data:',
          error
        );
      }
    },
    async fetchInvoicesReportData(channelId: number) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp = await pinia.$axios.$get<ReportResults<InvoicesDataItem>>(
          pinia.$API.channels.getInvoiceReportData(channelId)
        );
        this.invoiceReportData = assignNAValue(resp?.reportResults);
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching invoices report data:',
          error
        );
      }
    },
    async fetchPickupReportData(channelId: number) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp = await pinia.$axios.$get<ReportResults<PickupDataItem>>(
          pinia.$API.channels.getPickupReportData(channelId)
        );
        this.pickupReportData = assignNAValue(resp?.reportResults);
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching pickup report data:',
          error
        );
      }
    },
    clearReportsTables() {
      this.studyReportsData = [];
      this.rentalSummariesData = [];
      this.rentalEquipmentSummariesData = [];
      this.invoiceReportData = [];
      this.pickupReportData = [];
    },
    overrideEquipmentPaginator(paginator: Partial<ProductPaginator>) {
      this.studyEquipmentPaginator = {
        ...this.studyEquipmentPaginator,
        ...paginator,
      };
    },
    overrideSuppliesPaginator(paginator: Partial<ProductPaginator>) {
      this.studySuppliesPaginator = {
        ...this.studySuppliesPaginator,
        ...paginator,
      };
    },
    setActiveChannel(payload: Channel) {
      if (
        this.activeChannel &&
        this.activeChannel.channelId !== payload?.channelId
      ) {
        this.activeChannelMemberPostsFetched = false;
      }
      this.activeChannel = payload;
    },
    removeChannel(channelId: number) {
      this.channels = this.channels.filter((c) => c.channelId !== channelId);
    },
    updateChannelsCollection(channel: Channel) {
      const index = this.channels.findIndex(
        (c) => c.channelId === channel.channelId
      );

      if (index !== -1) {
        this.channels[index] = channel;
      } else {
        this.channels.push(channel);
      }
    },
    async getChannelSummary({
      countryId,
      channelId,
      availableRolesCategory,
    }: GetChannelSummary) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const Query = pinia.$Query;
        const resp = await pinia.$axios.$get(
          pinia.$API.channels.channelSummary(
            channelId,
            new Query({
              countryId,
              availableRolesCategory,
            }).getQueryParameters()
          )
        );

        let key = channelId;
        if (countryId) {
          key += `>${countryId}`;
        }
        this.channelSummary = {
          ...this.channelSummary,
          [key]: resp as Channel,
        };
        const channelIndex = this.channels.findIndex(
          (c) => c.channelId === Number(channelId)
        );
        if (channelIndex === -1) {
          this.channels.push(resp as Channel);
        } else {
          this.channels[channelIndex] = resp as Channel;
        }

        this.activeChannel = this.channelSummary[key] as Channel;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in fetching channel summary',
          error
        );
      }
    },
    async handleSubscription(channelId: number | string, subscriptionStatus) {
      const pinia = usePinia() as Pinia<API>;
      const api = pinia.$API.user.subscribeToChannel(channelId);

      try {
        if (!this.activeChannel.userRelatedData.userSubscribedToNotifications) {
          await pinia.$axios.$post(api);
        } else {
          await pinia.$axios.$delete(api);
        }
        this.activeChannel.userRelatedData.userSubscribedToNotifications =
          subscriptionStatus;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in handling subscription',
          error
        );
      }
    },
    async cachedChannelMembers({
      pageSize,
      sort,
      memberStatusType,
      channelId,
      search,
      page,
    }: GetStudyMembers) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const Query = pinia.$Query;
        const resp = await pinia.$axios.$post(
          pinia.$API.channels.channelMembers(
            channelId,
            new Query({
              pageSize,
              page,
              sort,
              memberStatusType,
              search,
            }).getQueryParameters()
          ),
          []
        );

        let key = '';
        switch (memberStatusType) {
          case 'CURRENT':
          case 'INVITED':
          case 'PAST':
          case 'ALL':
            key = `${channelId}>${memberStatusType}`;
            this.channelMembersCached = {
              ...this.channelMembersCached,
              [key]: resp,
            };
            break;
          default:
            break;
        }
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error caching channel members:',
          error
        );
      }
    },
    searchMembersTerm(payload: string) {
      this.memberSearchTerm = payload;
    },
    setMemberSort(payload: string) {
      this.memberSort = payload;
    },
    setActiveMemberGridKey(payload: string) {
      this.activeMemberGridKey = payload;
    },
    async setChannelTeamMembers({
      pageSize,
      sort,
      memberStatusType,
      channelId,
      search,
      page,
      teamId,
    }: GetStudyTeamMembers) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const Query = pinia.$Query;
        const resp = await pinia.$axios.$post(
          pinia.$API.channels.channelTeamMembers(
            channelId,
            teamId,
            new Query({
              pageSize,
              page,
              sort,
              memberStatusType,
              search,
            }).getQueryParameters()
          ),
          []
        );
        let key = '';
        switch (memberStatusType) {
          case 'CURRENT':
          case 'INVITED':
          case 'PAST':
          case 'ALL':
            key = `${channelId}>${teamId}>${memberStatusType}`;
            this.channelTeamMembers = {
              ...this.channelTeamMembers,
              [key]: resp,
            };
            break;
          default:
            break;
        }
        // this.channelTeamMembers = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in set channel team members',
          error
        );
      }
    },
    async channelMemberDetail({ channelId, memberId }) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp = await pinia.$axios.$get(
          pinia.$API.channels.channelMemberDetail(channelId, memberId)
        );
        const key = (channelId += `>${memberId}`);
        this.memberDetail = {
          ...this.memberDetail,
          [key]: new MemberDetail(resp as MemberDetail),
        };
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in channel member detail',
          error
        );
      }
    },
    async refetchAffectedMemberCaches(
      channelId: string | number,
      teamId?: string | number
    ) {
      const statuses = ['CURRENT', 'ALL', 'PAST'] as const;
      const tasks: Promise<unknown>[] = [];

      for (const status of statuses) {
        const key = `${channelId}>${status}`;
        const cached = this.channelMembersCached[key];
        if (cached) {
          const isActive = this.activeMemberGridKey === key;
          const search = isActive ? this.memberSearchTerm : '';
          const sort = isActive ? this.memberSort : '';
          const page = isActive ? cached.page : 1;
          const pageSize = isActive ? cached.pageSize : 50;
          tasks.push(
            this.cachedChannelMembers({
              channelId,
              memberStatusType: status,
              page,
              pageSize,
              sort,
              search,
            })
          );
        }

        if (teamId) {
          const teamKey = `${channelId}>${teamId}>${status}`;
          const teamCached = this.channelTeamMembers[teamKey];
          if (teamCached) {
            const isActive = this.activeMemberGridKey === teamKey;
            const search = isActive ? this.memberSearchTerm : '';
            const sort = isActive ? this.memberSort : '';
            const page = isActive ? teamCached.page : 1;
            const pageSize = isActive ? teamCached.pageSize : 50;
            tasks.push(
              this.setChannelTeamMembers({
                channelId,
                teamId,
                memberStatusType: status,
                page,
                pageSize,
                sort,
                search,
              })
            );
          }
        }
      }

      await Promise.all(tasks);
    },
    async refetchAffectedMemberDetail(
      channelId: string | number,
      memberId: string
    ) {
      const key = `${channelId}>${memberId}`;
      if (!this.memberDetail[key]) return;
      await this.channelMemberDetail({ channelId, memberId });
    },
    removeMemberDetail(channelId: number, memberId: string) {
      const key = `${channelId}>${memberId}`;

      const next = {};
      for (const k in this.memberDetail) {
        if (k !== key) {
          next[k] = this.memberDetail[k];
        }
      }

      this.memberDetail = next as DetailMember;
    },
    async getKeyContacts({
      channelId,
      page,
      pageSize,
    }: {
      channelId: string | number;
      page: number;
      pageSize: number;
    }) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const Query = pinia.$Query;
        const resp = await pinia.$axios.$get(
          pinia.$API.channels.channelKeyContacts(
            channelId,
            new Query({
              page,
              pageSize,
            }).getQueryParameters()
          )
        );
        const key = channelId;
        this.keyContacts = {
          ...this.keyContacts,
          [key]: resp as KeyContactsPage,
        };
      } catch (error) {
        handleErrorAndNotificationHandler('Error in get key contacts', error);
      }
    },
    /**
     * Sends an invitation to the user
     * @param {*} param0
     * @param { MemberInvite } memberInvite
     */
    async inviteNewMembers(memberInvite: MemberInvite) {
      try {
        await memberInvite.save();
        return { success: true };
      } catch (error) {
        let errorMessage = 'Unexpected error occurred.';

        if (error instanceof AxiosError) {
          if (error.response) {
            // Server responded with a status outside the 2xx range
            errorMessage = `Error: ${error.response.status} - ${error.response.statusText}`;
            handleErrorAndNotificationHandler(errorMessage, error);
            return {
              success: false,
              status: error.response.status,
              data: error.response.data,
            };
          } else if (error.request) {
            // No response received from the server
            errorMessage = 'No response received from the server.';
            console.error(errorMessage, error.request);
            return {
              success: false,
              error: errorMessage,
              request: error.request,
            };
          }
        }

        // Fallback for non-Axios errors
        if (error instanceof Error) {
          errorMessage = error.message;
        }

        handleErrorAndNotificationHandler('Error', errorMessage);
        return { success: false, error: errorMessage };
      }
    },

    async resendInvitation({
      teamId,
      channelId,
      inviteId,
      updatedEmailAddress,
    }: {
      teamId: string | number;
      channelId: string | number;
      inviteId: string;
      updatedEmailAddress: string;
    }) {
      const pinia = usePinia() as Pinia<API>;
      const Query = pinia.$Query;
      try {
        await pinia.$axios.$patch(
          pinia.$API.channels.resendInvitation(
            channelId,
            teamId,
            inviteId,
            new Query({
              updatedEmailAddress,
            }).getQueryParameters()
          )
        );
      } catch (error) {
        handleErrorAndNotificationHandler('Error resending invitation:', error);
      }
    },
    async updateInvitedMemberRole({
      teamId,
      channelId,
      inviteId,
      roleIds,
      sendEmailInvitation,
      email,
      invitedUserFirstName,
      invitedUserLastName,
    }: {
      teamId: number;
      channelId: string | number;
      inviteId: number;
      roleIds: number[];
      sendEmailInvitation: boolean;
      email: string;
      invitedUserFirstName: string;
      invitedUserLastName: string;
    }) {
      const pinia = usePinia() as Pinia<API>;
      try {
        await pinia.$axios.$put(
          pinia.$API.channels.updateInvitedMemberRole(
            channelId,
            teamId,
            inviteId
          ),
          {
            sendEmailInvitation,
            roleIds,
            email,
            invitedUserFirstName,
            invitedUserLastName,
          }
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in update invited member role',
          error
        );
      }
    },
    inviteMemberMessage(inviteMessage: string) {
      this.inviteMessage = inviteMessage;
    },
    async removeMemberFromChannel({ channelId, memberId }) {
      // this should be moved to members store - maybe
      const pinia = usePinia() as Pinia<API>;
      try {
        await pinia.$axios.$delete(
          pinia.$API.channels.channelMemberDetail(channelId, memberId)
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in removing member from channel',
          error
        );
      }
    },
    async removeMemberFromTeam({
      channelId,
      teamId,
      memberId,
    }: {
      channelId: string | number;
      teamId: number;
      memberId: string;
    }) {
      // this should be moved to members store - maybe
      const pinia = usePinia() as Pinia<API>;
      try {
        await pinia.$axios.$delete(
          pinia.$API.channels.removeMemberFromTeam(channelId, teamId, memberId)
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error when removing member from team',
          error
        );
      }
    },
    async invitedMemberDetail({
      channelId,
      invitedMemberEmail,
    }: {
      channelId: string | number;
      invitedMemberEmail: string;
    }) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp = await pinia.$axios.$get(
          pinia.$API.channels.invitedChannelMemberDetail(
            channelId,
            invitedMemberEmail
          )
        );
        const key = (channelId += `>${invitedMemberEmail}`);
        this.memberDetailInvited = {
          ...(typeof this.memberDetailInvited === 'object' &&
          this.memberDetailInvited !== null
            ? this.memberDetailInvited
            : {}),
          [key]: resp,
        };
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in invited Member Detail',
          error
        );
      }
    },
    async updateMemberRoles({
      channelId,
      memberId,
      roleId,
    }: {
      channelId: string;
      memberId: string;
      roleId: number[];
    }) {
      // this should be moved to members store
      const pinia = usePinia() as Pinia<API>;
      const rolesString = `${roleId.map((id) => `roleId=${id}&`)}`;
      try {
        const query = `?${rolesString}`;
        await pinia.$axios.$put(
          pinia.$API.channels.channelMemberRoles(
            channelId,
            memberId,
            query.replace(/,/g, '').slice(0, -1)
          )
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in update member roles',
          error
        );
      }
    },
    async getMemberRoles({
      channelId,
      memberId,
    }: {
      channelId: string;
      memberId: string;
    }) {
      // this should be moved to members store
      // this is same as action channelMemberRoles which is not added here
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp = await pinia.$axios.$get(
          pinia.$API.channels.getChannelMemberRoles(channelId, memberId)
        );
        this.memberRoles = resp as ChannelRole[];
      } catch (error) {
        handleErrorAndNotificationHandler('Error fetching member roles', error);
      }
    },
    async fetchChannelRoles(channelId: number | string) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp = await pinia.$axios.$get(
          pinia.$API.channels.getChannelRoles(channelId)
        );
        this.channelRoles = resp as ChannelRole[];
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in fetching channel roles',
          error
        );
      }
    },
    async channelMemberRoles({
      channelId,
      memberId,
    }: {
      channelId: string;
      memberId: string;
    }) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp = await pinia.$axios.$get(
          pinia.$API.channels.getChannelMemberRoles(channelId, memberId)
        );
        this.memberRoles = resp as ChannelRole[];
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in fetching channel member roles',
          error
        );
      }
    },

    async updateChannel(activeChannel: Channel) {
      const channelId = activeChannel.channelId;
      const formData = new FormData();

      const stringify = JSON.stringify({
        title: activeChannel.briefTitle,
        name: activeChannel.name,
        bannerColorCSS: activeChannel.channelBanner,
      });
      formData.append('channelMetadataRequest', stringify);
      if (activeChannel.logoUrl) {
        const blobLogo = activeChannel.logoUrl;
        formData.append('logoFile', blobLogo);
      }

      if (activeChannel.bannerFile) {
        const blobBanner = activeChannel.bannerFile;
        formData.append('bannerFile', blobBanner);
      }
      const pinia = usePinia() as Pinia<API>;
      try {
        await pinia.$axios.$patch(
          pinia.$API.channels.updateChannel(channelId),
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } catch (error) {
        handleErrorAndNotificationHandler('Error in updating channel', error);
      }
    },
    async getUserChannels() {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp: ClientOwnedChannel = await pinia.$axios.$get(
          pinia.$API.user.getCollectionOfChannels()
        );
        this.clientChannels = resp?.items;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in fetching user channels',
          error
        );
      }
    },
    async getStudyNotificationContacts(channelId: number | string) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp: NotificationResp = await pinia.$axios.$get(
          pinia.$API.channels.studyNotificationContact(channelId)
        );

        this.studyNotificationContacts = resp?.notificationContactConfigs;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in fetching study contacts',
          error
        );
      }
    },
    resetStudyContacts() {
      this.studyNotificationContacts = [];
      this.teamNotificationContacts = [];
    },
    async addStudyNotificationContact(
      channelId: number | string,
      payload: SiteContact
    ) {
      const pinia = usePinia() as Pinia<API>;
      try {
        await pinia.$axios.$post(
          pinia.$API.channels.studyNotificationContact(channelId),
          payload
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in creating study contact',
          error
        );
      }
    },
    async removeStudyNotificationContact(
      channelId: string | number,
      notificationContactId: number
    ) {
      const pinia = usePinia() as Pinia<API>;
      try {
        await pinia.$axios.$delete(
          pinia.$API.channels.updateStudyNotificationContact(
            channelId,
            notificationContactId
          )
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in removing study contact',
          error
        );
      }
    },
    async updateStudyNotificationContact(
      channelId: string | number,
      notificationContactId: number,
      payload: StudyContact
    ) {
      const pinia = usePinia() as Pinia<API>;
      try {
        await pinia.$axios.$put(
          pinia.$API.channels.updateStudyNotificationContact(
            channelId,
            notificationContactId
          ),
          payload
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in updating study contact',
          error
        );
      }
    },
    async createTeamNotificationContact(
      studyId: string | number,
      teamId: string | number,
      payload: SiteContact
    ) {
      const pinia = usePinia() as Pinia<API>;
      try {
        await pinia.$axios.$post(
          pinia.$API.channels.studyTeamContacts(studyId, teamId),
          payload
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in creating team notification contact',
          error
        );
      }
    },
    async getTeamNotificationContacts(
      studyId: string | number,
      teamId: string | number
    ) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp: NotificationResp = await pinia.$axios.$get(
          pinia.$API.channels.studyTeamContacts(studyId, teamId)
        );

        this.teamNotificationContacts = resp.notificationContactConfigs;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in fetching team notification contacts',
          error
        );
      }
    },
    async removeTeamNotificationContacts(
      studyId: string | number,
      teamId: string | number,
      notificationId: string | number
    ) {
      const pinia = usePinia() as Pinia<API>;
      try {
        await pinia.$axios.$delete(
          pinia.$API.channels.updateDeleteStudyTeamContacts(
            studyId,
            teamId,
            notificationId
          )
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in deleting deleting team notification contact',
          error
        );
      }
    },
    async updateTeamNotificationContacts(
      studyId: string | number,
      teamId: string | number,
      notificationId: string | number,
      payload: SiteContact
    ) {
      const pinia = usePinia() as Pinia<API>;
      try {
        await pinia.$axios.$put(
          pinia.$API.channels.updateDeleteStudyTeamContacts(
            studyId,
            teamId,
            notificationId
          ),
          payload
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in updating team notification contact',
          error
        );
      }
    },
    async getConfiguredNotificationContacts(
      studyId: string | number,
      payload: { serviceEntityType: string; teamId: string | number }
    ) {
      const pinia = usePinia() as Pinia<API>;
      const Query = pinia.$Query;
      const { serviceEntityType, teamId } = payload;
      try {
        const resp: WorkflowNotificationContactConfigResp =
          await pinia.$axios.$get(
            pinia.$API.channels.getConfiguredNotificationContacts(
              studyId,
              new Query({
                ...(teamId != null ? { teamId } : {}),
                serviceEntityType,
              }).getTeamQueryParameters()
            )
          );

        this.configuredNotificationContacts =
          resp.workflowNotificationContactConfigs;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in fetching configured notification contacts',
          error
        );
      }
    },

    async getStudyQuotations(studyId: string | number) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp: QuotationResp | null = await pinia.$axios.$get(
          pinia.$API.channels.getStudyQuotations(studyId)
        );

        const mapped = Array.isArray(resp?.quotations)
          ? resp.quotations.map((q) => ({
              ...q,
              createDate: DateTime.fromMillis(q.createDateMillis).toFormat(
                'dd-MMM-yyyy'
              ),
              expirationDate:
                q.expirationDateMillis !== 0
                  ? DateTime.fromMillis(q.expirationDateMillis).toFormat(
                      'dd-MMM-yyyy'
                    )
                  : 'N/A',
              total: `${q.currencyCode || ''} ${q.total || '0'}`,
              totalValueNumber: parseFloat((q.total || '0').replace(/,/g, '')),
            }))
          : [];

        this.studyQuotations = mapped;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in fetching study quotations',
          error
        );
        this.studyQuotations = [];
      }
    },

    async fetchtRentalAgreementtForStudy(studyId: string | number) {
      const pinia = usePinia() as Pinia<API>;

      try {
        const resp: RentalResponse | null = await pinia.$axios.$get(
          pinia.$API.channels.getRentalAgreementtForStudy(studyId)
        );

        const agreements = Array.isArray(resp?.rentalAgreements)
          ? resp.rentalAgreements
          : [];

        const mapped = agreements.map((a) => ({
          ...a,
          startingDate: DateTime.fromMillis(a.startDateMillis).toFormat(
            'dd-MMM-yyyy'
          ),
          total: `${a.currency} ${a.totalDisplay}`,
          totalValueNumber: parseFloat(a.totalDisplay.replace(/,/g, '')),
        }));

        this.rentalAgreement = mapped;
      } catch (error) {
        this.rentalAgreement = [];

        const axiosError = error as AxiosError;

        if (axiosError.response?.status !== 500) {
          handleErrorAndNotificationHandler(
            'Error fetching rental agreements',
            axiosError
          );
        }
      }
    },

    async fetchStudyInvoices(studyId: string | number) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp: InvoiceResponse = await pinia.$axios.$get(
          pinia.$API.channels.getInvoicesForStudy(studyId)
        );
        const mapped = resp.invoices.map((i) => ({
          ...i,
          invoiceDate: DateTime.fromMillis(i.invoiceDateMillis).toFormat(
            'dd-MMM-yyyy'
          ),
          dueDate: DateTime.fromMillis(i.dueDateMillis).toFormat('dd-MMM-yyyy'),
          total: `${i.currency} ${i.totalDisplay}`,
          amountDue: `${i.currency} ${i.amountDueDisplay}`,
          totalNumber: parseFloat(i.totalDisplay.replace(/,/g, '')),
          amountDueNumber: parseFloat(i.amountDueDisplay.replace(/,/g, '')),
        }));
        this.studyInvoices = mapped === undefined ? [] : mapped;
      } catch (error) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'response' in error
        ) {
          const status = (error as AxiosError).response?.status;

          if (status === 500) {
            this.studyInvoices = [];
            return;
          }
        }

        this.studyInvoices = [];
        handleErrorAndNotificationHandler('Error fetching invoices', error);
      }
    },
    async getStudyQuotationPdf(
      studyId: number | string,
      quotationId: number | string
    ) {
      const pinia = usePinia() as Pinia<API>;
      try {
        await pinia.$axios.$get(
          pinia.$API.channels.getStudyQuotationPdf(studyId, quotationId)
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in fetching quotation pdf',
          error
        );
      }
    },
    async fetchRentalEquipmentItems(
      channelId: string | number,
      query: {
        teamId: number | string;
        search: string;
        page: number;
        pageSize: number;
      }
    ) {
      const pinia = usePinia() as Pinia<API>;
      const Query = pinia.$Query;

      try {
        const resp: RentalEquipment = await pinia.$axios.$get(
          pinia.$API.channels.getRentalEquipmentData(
            channelId,
            new Query({
              teamId: query.teamId,
              search: query.search,
              page: query.page,
              pageSize: query.pageSize,
            }).getQueryParameters()
          )
        );
        this.rentalEquipment = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in fetching product data',
          error
        );
      }
    },
    async getAllRentalEquipmentItems(
      channelId: string | number,
      teamId?: string | number
    ): Promise<RentalEquipmentItem[]> {
      const pinia = usePinia() as Pinia<API>;
      const Query = pinia.$Query;
      const equipmentItems: RentalEquipmentItem[] = [];
      const pageSize = 100;
      let page = 1;
      let isLastPage = false;

      while (!isLastPage) {
        const response = await pinia.$axios.$get<RentalEquipment>(
          pinia.$API.channels.getRentalEquipmentData(
            channelId,
            new Query({
              ...(teamId !== undefined ? { teamId } : {}),
              search: '',
              page,
              pageSize,
            }).getQueryParameters()
          )
        );

        if (
          !Array.isArray(response.rentalEquipmentItems) ||
          !response.rentalEquipmentItems.length
        ) {
          break; //stop rather than loop forever
        }

        equipmentItems.push(...response.rentalEquipmentItems);
        isLastPage = response.isLastPage;
        page++;
      }

      return equipmentItems;
    },
    async fetchAllStudyRentalEquipmentItems(channelId: string | number) {
      this.allStudyRentalEquipmentLoading = true;
      this.allStudyRentalEquipmentError = false;
      this.allStudyRentalEquipment = [];

      try {
        this.allStudyRentalEquipment =
          await this.getAllRentalEquipmentItems(channelId);
      } catch (error) {
        this.allStudyRentalEquipment = [];
        this.allStudyRentalEquipmentError = true;
        handleErrorAndNotificationHandler(
          'Error fetching study rental equipment:',
          error
        );
      } finally {
        this.allStudyRentalEquipmentLoading = false;
      }
    },
    productsNextPageSync(value: boolean, type: string) {
      if (type === 'EQUIPMENT') {
        this.studyEquipmentSyncing = value;
      } else {
        this.studySuppliesSyncing = value;
      }
    },

    async fetchProductData({
      channelId,
      payload,
    }: {
      channelId: string | number;
      payload: {
        productType: string;
        page: number;
        pageSize: number;
        search: string;
        teamId: number[];
      };
    }) {
      const pinia = usePinia() as Pinia<API>;
      const Query = pinia.$Query;

      try {
        let studyTeamIdParams = '';
        if (Array.isArray(payload.teamId)) {
          studyTeamIdParams = payload.teamId
            .map((id: number, index) => {
              if (index === 0) {
                return `${id}`;
              } else {
                return `teamId=${id}`;
              }
            })
            .join('&');
        } else if (
          typeof payload.teamId === 'string' ||
          typeof payload.teamId === 'number'
        ) {
          studyTeamIdParams = `${payload.teamId}`;
        }

        const resp: StudyProductsResp = await pinia.$axios.$get(
          pinia.$API.channels.getProductData(
            channelId,
            new Query({
              productType: payload.productType,
              teamId: studyTeamIdParams,
              page: payload.page,
              pageSize: payload.pageSize,
              search: payload.search,
            }).getQueryParameters()
          )
        );
        const { products, ...paginator } = resp;
        if (payload.productType === 'EQUIPMENT') {
          this.studyEquipment = products;
          this.studyEquipmentPaginator = paginator;
        } else if (payload.productType === 'SUPPLIES') {
          this.studySupplies = products;
          this.studySuppliesPaginator = paginator;
        }
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in fetching product data',
          error
        );
      }
    },
    async fetchProductDataNextPage({
      channelId,
      payload,
    }: {
      channelId: string | number;
      payload: {
        productType: string;
        page: number;
        pageSize: number;
        search: string;
        teamId: number[];
      };
    }) {
      const pinia = usePinia() as Pinia<API>;
      const Query = pinia.$Query;
      try {
        let studyTeamIdParams = '';
        if (Array.isArray(payload.teamId)) {
          studyTeamIdParams = payload.teamId
            .map((id: number, index) => {
              if (index === 0) {
                return `${id}`;
              } else {
                return `teamId=${id}`;
              }
            })
            .join('&');
        } else if (
          typeof payload.teamId === 'string' ||
          typeof payload.teamId === 'number'
        ) {
          studyTeamIdParams = `${payload.teamId}`;
        }

        const resp: StudyProductsResp = await pinia.$axios.$get(
          pinia.$API.channels.getProductData(
            channelId,
            new Query({
              productType: payload.productType,
              teamId: studyTeamIdParams,
              page: payload.page,
              pageSize: payload.pageSize,
              search: payload.search,
            }).getQueryParameters()
          )
        );
        const { products, ...paginator } = resp;
        if (payload.productType === 'EQUIPMENT') {
          this.studyEquipment = [...this.studyEquipment, ...products];
          this.studyEquipmentPaginator = paginator;
        } else if (payload.productType === 'SUPPLIES') {
          this.studySupplies = [...this.studySupplies, ...products];
          this.studySuppliesPaginator = paginator;
        }
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in fetching product data',
          error
        );
      }
    },
    cleanUpProducts() {
      this.studyEquipment = [];
      this.studySupplies = [];
      this.studyEquipmentPaginator = {} as ProductPaginator;
      this.studySuppliesPaginator = {} as ProductPaginator;
    },
    async createCatalogOrder(
      channelId: string | number,
      payload: OrderPayload
    ) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const response: CreateCatalogOrderStatus = await pinia.$axios.$post(
          pinia.$API.channels.submitCatalogSalesOrder(channelId),
          payload
        );

        return typeof response.status === 'number' ? response.status : 200;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error during creation of catalog order',
          error
        );

        if (
          error &&
          typeof error === 'object' &&
          'response' in error &&
          error.response &&
          typeof error.response === 'object' &&
          'status' in error.response &&
          typeof error.response.status === 'number'
        ) {
          return error.response.status;
        }

        return 500;
      }
    },

    async createQuotationOrder(
      channelId: string | number,
      payload: OrderPayload
    ) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const response: CreateCatalogOrderStatus = await pinia.$axios.$post(
          pinia.$API.channels.submitQuotationSalesOrder(channelId),
          payload
        );

        return typeof response.status === 'number' ? response.status : 200;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error during creation of quotation order',
          error
        );

        if (
          error &&
          typeof error === 'object' &&
          'response' in error &&
          error.response &&
          typeof error.response === 'object' &&
          'status' in error.response &&
          typeof error.response.status === 'number'
        ) {
          return error.response.status;
        }

        return 500;
      }
    },

    async verifyEquipmentAndSupplies(
      channelId: string | number,
      teamId: string | number
    ) {
      const pinia = usePinia() as Pinia<API>;
      const Query = pinia.$Query;
      try {
        const response: AxiosResponse<CreateCatalogOrderStatus> =
          await pinia.$axios.get(
            pinia.$API.channels.verifyMultipleSites(
              channelId,
              new Query({
                ...(teamId != null ? { teamId } : {}),
              }).getTeamQueryParameters()
            )
          );
        console.log('response', response);

        return response.status;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error during equipment and supplies verification',
          error
        );
        if (
          typeof error === 'object' &&
          error !== null &&
          'response' in error &&
          typeof error.response === 'object' &&
          error.response !== null &&
          'status' in error.response
        ) {
          return error.response.status;
        }
        return 500;
      }
    },
    async createSupportRequest(
      channelId: string | number,
      payload: {
        summary: string;
        reason: string;
        productName: string;
        twoDigitCountryCode: string;
        siteCode: string;
        studyName: string;
        serialOrLotNumber: string;
        contactName: string;
        contactPhoneNumber: string;
        contactEmailAddress: string;
        detailText: string;
        attachments: AttachmentFile[];
      }
    ) {
      let isRequestSuccessfull = false;
      const formData = new FormData();
      formData.append('summary', payload.summary);
      formData.append('reason', payload.reason);
      formData.append('productName', payload.productName);
      formData.append('twoDigitCountryCode', payload.twoDigitCountryCode);
      formData.append('siteCode', payload.siteCode);
      formData.append('studyName', payload.studyName);
      formData.append('serialOrLotNumber', payload.serialOrLotNumber);
      formData.append('contactName', payload.contactName);
      formData.append('contactPhoneNumber', payload.contactPhoneNumber);
      formData.append('contactEmailAddress', payload.contactEmailAddress);
      formData.append('detailText', payload.detailText);
      payload.attachments?.forEach((fileObj, index) => {
        const rawFile = fileObj.getRawFile?.();
        if (rawFile instanceof File) {
          formData.append(`attachment${index + 1}`, rawFile, rawFile.name);
        }
      });
      const pinia = usePinia() as Pinia<API>;
      try {
        await pinia.$axios.$post(
          pinia.$API.channels.supportRequest(channelId),
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        isRequestSuccessfull = true;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error during support request creation',
          error
        );
      }
      return isRequestSuccessfull;
    },
    async fetchAndSetActiveChannel(channelId: number | string): Promise<void> {
      const pinia = usePinia() as Pinia<API>;

      const id =
        typeof channelId === 'string' ? parseInt(channelId, 10) : channelId;

      let channel = this.channels.find((c) => c.channelId === id);

      if (!channel) {
        try {
          channel = await pinia.$axios.$get<Channel>(
            pinia.$API.channels.getChannel(channelId)
          );

          if (channel) {
            this.channels.push(channel);
          }
        } catch (error) {
          handleErrorAndNotificationHandler('Error fetching channel', error);
          return;
        }
      }

      if (channel) {
        this.setActiveChannel(channel);
      }
    },
    async removeBanner(channelId: number | string): Promise<void> {
      const pinia = usePinia() as Pinia<API>;

      try {
        await pinia.$axios.$delete(
          pinia.$API.channels.removeBannerImage(channelId)
        );
      } catch (error) {
        handleErrorAndNotificationHandler('Error removing banner', error);
      }
    },
    async removeLogo(channelId: number | string): Promise<void> {
      const pinia = usePinia() as Pinia<API>;

      try {
        await pinia.$axios.$delete(
          pinia.$API.channels.removeLogoImage(channelId)
        );
      } catch (error) {
        handleErrorAndNotificationHandler('Error removing logo', error);
      }
    },
    async patchStudy({
      channelId,
      payload,
    }: {
      channelId: number | string;
      payload: Record<string, unknown>;
    }) {
      const pinia = usePinia() as Pinia<API>;
      try {
        await pinia.$axios.$patch(
          pinia.$API.app.updateStudyConfig(channelId),
          payload
        );
      } catch (error) {
        handleErrorAndNotificationHandler('Error patching study:', error);
      }
    },
    async updateChannelStatus(
      channelId: number | string,
      transitionName: string
    ) {
      const pinia = usePinia() as Pinia<API>;

      try {
        const query = new pinia.$Query({ transitionName });
        await pinia.$axios.$post(
          pinia.$API.channels.updateChannelStatus(
            channelId,
            query.getTeamQueryParameters()
          )
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error updating channel status:',
          error
        );
      }
    },
    setSelectedEquipmentItem(item: RentalEquipmentItem | null) {
      this.selectedEquipmentItem = item;
      if (item && this.rentalEquipment) {
        this.rentalEquipment.rentalEquipmentItems = [item];
        this.rentalEquipment.totalCount = 1;
      }
    },

    async ensureEquipmentItemLoaded(
      channelId: number | string,
      equipmentId: string
    ) {
      const targetId = String(equipmentId).trim();

      if (!targetId) {
        this.selectedEquipmentItem = null;
        if (this.rentalEquipment) {
          this.rentalEquipment.rentalEquipmentItems = [];
          this.rentalEquipment.totalCount = 0;
        }
        return null;
      }

      const isTargetItem = (item: RentalEquipmentItem) => {
        const serviceItemId =
          item.serviceItemId != null ? String(item.serviceItemId).trim() : '';
        const serialOrLotNumber =
          item.serialOrLotNumber != null
            ? String(item.serialOrLotNumber).trim()
            : '';
        return serviceItemId === targetId || serialOrLotNumber === targetId;
      };

      let foundItem =
        this.selectedEquipmentItem && isTargetItem(this.selectedEquipmentItem)
          ? this.selectedEquipmentItem
          : this.rentalEquipment?.rentalEquipmentItems?.find(isTargetItem);

      if (!foundItem) {
        try {
          await this.fetchRentalEquipmentItems(channelId, {
            teamId: '',
            search: targetId,
            page: 1,
            pageSize: 100,
          });

          foundItem =
            this.rentalEquipment?.rentalEquipmentItems?.find(isTargetItem);
        } catch (error) {
          console.error('Failed to load equipment item in store:', error);
          foundItem = undefined;
        }
      }
      if (foundItem) {
        this.selectedEquipmentItem = foundItem;
        if (this.rentalEquipment) {
          this.rentalEquipment.rentalEquipmentItems = [foundItem];
          this.rentalEquipment.totalCount = 1;
        }
      } else {
        this.selectedEquipmentItem = null;
        if (this.rentalEquipment) {
          this.rentalEquipment.rentalEquipmentItems = [];
          this.rentalEquipment.totalCount = 0;
        }
      }

      return this.selectedEquipmentItem;
    },
  },

  getters: {
    getActiveChannelGetter: (state) => state.activeChannel,
    getMemberDetailGetter: (state) => state.memberDetail,
    getChannelsGetter: (state) => {
      return state.channels;
    },
    getStudyNotificationContactsGetter: (state) =>
      state.studyNotificationContacts,
    getTeamNotificationContactsGetter: (state) =>
      state.teamNotificationContacts,
    getConfigNotificationContactsGetter: (state) =>
      state.configuredNotificationContacts,
    equipmentLoggerSettingsGetter: (state) =>
      state.studyEquipmentPaginator.productLoggerSettings
        ?.preconfiguredSettings ?? [],
  },
});
